import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import ErrorIcon from '@mui/icons-material/Error'; // 現在未使用
import Copyright from '../molecules/Copyright';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
}));

const AmountDisplay = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const RetryButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: '1.1rem',
  fontWeight: 'bold',
}));

interface PaymentInfo {
  payment_intent_id: string;
  amount: number;
  currency: string;
  status: string;
  failure_reason: string;
  created: number;
  description: string;
}

interface ProcessingStatus {
  isLoading: boolean;
  isProcessing: boolean;
  isCompleted: boolean;
  error: string | null;
}

const RetryPaymentDemo: React.FC = () => {
  // デモ用のモックデータ
  const mockPaymentInfo: PaymentInfo = {
    payment_intent_id: 'pi_demo_123456789',
    amount: 1980,
    currency: 'jpy',
    status: 'requires_action',
    failure_reason: '3D認証が必要です',
    created: Math.floor(Date.now() / 1000) - 3600, // 1時間前
    description: 'TRANSREC月額プラン',
  };

  const [paymentInfo] = useState<PaymentInfo>(mockPaymentInfo);
  const [status, setStatus] = useState<ProcessingStatus>({
    isLoading: false,
    isProcessing: false,
    isCompleted: false,
    error: null,
  });
  const [demoStep, setDemoStep] = useState<'normal' | 'processing' | 'completed' | 'error'>('normal');

  // 金額のフォーマット
  const formatAmount = (amount: number, currency: string) => {
    if (currency.toLowerCase() === 'jpy') {
      return `¥${amount.toLocaleString()}`;
    }
    return `${amount} ${currency.toUpperCase()}`;
  };

  // 日時のフォーマット
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // デモ用の処理
  const handleRetryPaymentDemo = async () => {
    setDemoStep('processing');
    setStatus(prev => ({ ...prev, isProcessing: true, error: null }));

    // 3秒後に成功状態に変更
    setTimeout(() => {
      setDemoStep('completed');
      setStatus(prev => ({
        ...prev,
        isProcessing: false,
        isCompleted: true,
      }));
    }, 3000);
  };

  const handleShowError = () => {
    setDemoStep('error');
    setStatus(prev => ({
      ...prev,
      isProcessing: false,
      error: '決済処理中にエラーが発生しました。しばらく経ってから再度お試しください。',
    }));
  };

  const resetDemo = () => {
    setDemoStep('normal');
    setStatus({
      isLoading: false,
      isProcessing: false,
      isCompleted: false,
      error: null,
    });
  };

  if (demoStep === 'completed') {
    return (
      <Container maxWidth="md">
        <StyledPaper>
          <Box textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              再決済が完了しました
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              決済が正常に処理されました。ご利用いただきありがとうございます。
            </Typography>
            <Button
              variant="contained"
              onClick={resetDemo}
              sx={{ mt: 2, mr: 2 }}
            >
              デモをリセット
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.location.href = 'https://transrec.net'}
              sx={{ mt: 2 }}
            >
              トップページに戻る
            </Button>
          </Box>
        </StyledPaper>
        <Copyright />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <StyledPaper>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={2}>
              <CreditCardIcon sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                再決済のお手続き
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              決済に失敗したお支払いの再処理を行います。
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  決済情報
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    決済金額
                  </Typography>
                  <AmountDisplay>
                    {formatAmount(paymentInfo.amount, paymentInfo.currency)}
                  </AmountDisplay>
                </Box>

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    決済日時
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(paymentInfo.created)}
                  </Typography>
                </Box>

                {paymentInfo.description && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      説明
                    </Typography>
                    <Typography variant="body1">
                      {paymentInfo.description}
                    </Typography>
                  </Box>
                )}

                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    失敗理由
                  </Typography>
                  <Typography variant="body1" color="error.main">
                    {paymentInfo.failure_reason}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {status.error && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {status.error}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box textAlign="center">
              <RetryButton
                variant="contained"
                size="large"
                startIcon={status.isProcessing ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRetryPaymentDemo}
                disabled={status.isProcessing}
                sx={{ mr: 2 }}
              >
                {status.isProcessing ? '処理中...' : '再決済を行う'}
              </RetryButton>
              
              <Button
                variant="outlined"
                color="error"
                onClick={handleShowError}
                disabled={status.isProcessing}
                sx={{ mr: 2 }}
              >
                エラーを表示
              </Button>

              <Button
                variant="text"
                onClick={resetDemo}
                disabled={status.isProcessing}
              >
                リセット
              </Button>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ※ 再決済処理には3D認証が必要な場合があります
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </StyledPaper>
      <Copyright />
    </Container>
  );
};

export default RetryPaymentDemo;